import React, { Component } from "react";
import { Query } from "react-apollo";
import { withRouter } from "react-router";
import { Row, Col, Input } from "antd";
import { css } from "glamor";
import { Tunnel } from "react-tunnels";

import { formatDatetimeString } from "@arachnys/flow-app/src/formatters/formatDateTimeString";
import { DEFAULT_PAGESIZE } from "@arachnys/flow-app/src/config";
import ApolloPaginationTable from "@arachnys/flow-app/src/container/ApolloPaginationTable";
import { DocumentLink } from "@arachnys/flow-app/src/container/Documents";
import { debounce, decodeId } from "@arachnys/flow-app/src/util";
import { GET_BULK_UPLOADS, GET_BULK_PERMISSIONS } from "./queries";
import CommentsPopup from "./BulkRequest/CommentsPopup";
import AdditionalFilesPopup from "./BulkRequest/AdditionalFilesPopup";
import ChangeStatusButton from "./BulkRequest/ChangeStatusButton";
import { statuses } from "./BulkRequest/config";
import UploadRequestButton from "./BulkRequest/UploadRequestButton";

const styles = {
    searchInput: css({
        width: 350,
    }),
    tr: css({
        cursor: "pointer",
    }),
    centeredTd: css({
        textAlign: "center",
    }),
};

const columns = (isCustomer, refetch) => [
    {
        Header: "Name",
        id: "name",
        accessor: "node.name",
    },
    ...(isCustomer
        ? [
              {
                  Header: "File name",
                  id: "filename",
                  maxWidth: 250,
                  Cell: row => {
                      const fileList = row.original.node.fileUploads;
                      const file =
                          fileList.length && fileList[fileList.length - 1];
                      return (
                          file && (
                              <DocumentLink
                                  documentDatum={{
                                      documentId: file.docstoreId,
                                      documentName: file.fileName,
                                  }}
                              />
                          )
                      );
                  },
              },
          ]
        : []),
    {
        Header: "Upload by",
        id: "uploadby",
        maxWidth: 150,
        Cell: row =>
            `${row.original.node.requester.firstName} ${row.original.node.requester.lastName}`,
    },
    {
        Header: "Date Created",
        id: "uploaddate",
        maxWidth: 150,
        accessor: "node.createdDate",
        Cell: row =>
            formatDatetimeString({ str: row.original.node.createdDate }),
    },
    {
        Header: "Status",
        id: "status",
        maxWidth: 100,
        accessor: "node.status",
    },
    ...(isCustomer
        ? [
              {
                  Header: "",
                  id: "comments",
                  width: 100,
                  className: `${styles.centeredTd}`,
                  Cell: row => <CommentsPopup request={row.original.node} />,
              },
              {
                  Header: "",
                  id: "results",
                  width: 150,
                  className: `${styles.centeredTd}`,
                  Cell: row => {
                      const requestId = decodeId(row.original.node.id);
                      switch (row.original.node.status) {
                          case statuses.SUBMITTED:
                              return (
                                  <ChangeStatusButton
                                      icon="delete"
                                      action={statuses.CANCELED}
                                      deleteConfirm
                                      requestId={requestId}
                                      onSuccess={refetch}
                                  />
                              );
                          case statuses.RELEASED:
                              return (
                                  <AdditionalFilesPopup
                                      request={row.original.node}
                                  />
                              );
                          default:
                              return null;
                      }
                  },
              },
          ]
        : []),
];

export const canViewDetails = permissions =>
    permissions.canAssign ||
    permissions.canRelease ||
    permissions.canAccept ||
    permissions.canComplete;

class BulkUploadsPage extends Component {
    state = {
        filters: {},
    };

    handleSearchNameFilter = e => {
        this.setFilters({ name: e.target.value });
    };

    setFilters = (params = {}) => {
        this.setState(
            {
                filters: {
                    ...this.state.filters,
                    ...params,
                },
            },
            () => {
                this._applyWithDebounce(
                    this.props.apolloProps.refetch,
                    this.state.filters
                );
            }
        );
    };

    _applyWithDebounce = debounce((fn, args) => {
        fn(args);
    }, 500);

    openRequestDetails = (row, column) => () => {
        if (
            column.id !== "delete" ||
            row.original.node.status !== statuses.SUBMITTED
        ) {
            this.props.history.push(`/bulkrequest/${row.original.node.id}/`);
        }
    };

    render() {
        return (
            <>
                <Tunnel id="create-bulk-request">
                    <UploadRequestButton
                        onSuccess={this.props.apolloProps.refetch}
                    />
                </Tunnel>
                <Tunnel id="bluebar-left-content">
                    <Row>
                        <Col span={8}>
                            <Input.Search
                                placeholder="Search Requests"
                                onChange={this.handleSearchNameFilter}
                                size="large"
                                className={`${styles.searchInput}`}
                            />
                        </Col>
                    </Row>
                </Tunnel>
                <Query query={GET_BULK_PERMISSIONS} fetchPolicy="cache-only">
                    {({
                        data: {
                            ticketingPermissions: { bulkUploadRequest },
                        },
                    }) => {
                        return (
                            <ApolloPaginationTable
                                getTdProps={(state, row, column) => {
                                    return (
                                        canViewDetails(bulkUploadRequest) && {
                                            className: `${styles.tr}`,
                                            onClick: this.openRequestDetails(
                                                row,
                                                column
                                            ),
                                        }
                                    );
                                }}
                                apolloProps={this.props.apolloProps}
                                columns={columns(
                                    !canViewDetails(bulkUploadRequest),
                                    this.props.apolloProps.refetch
                                )}
                            />
                        );
                    }}
                </Query>
            </>
        );
    }
}

const BulkUploadPageRouter = withRouter(BulkUploadsPage);

const BulkUploads = () => (
    <Query
        query={GET_BULK_UPLOADS}
        fetchPolicy="cache-and-network"
        variables={{
            count: DEFAULT_PAGESIZE,
            cursor: null,
            uploadType: "Create Entities",
        }}
    >
        {apolloProps => <BulkUploadPageRouter apolloProps={apolloProps} />}
    </Query>
);

export default BulkUploads;
